import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, mergeProps as _mergeProps, toDisplayString as _toDisplayString, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "menu-calendar-settings" }
const _hoisted_2 = { class: "menu-calendar-settings__top-row" }
const _hoisted_3 = { class: "menu-calendar-settings__top-row__calendar" }
const _hoisted_4 = { class: "menu-calendar-settings__top-row__checkbox" }
const _hoisted_5 = { class: "menu-calendar-settings__top-row__quick-select" }
const _hoisted_6 = { class: "menu-calendar-settings__top-row__quick-select__header" }
const _hoisted_7 = { class: "menu-calendar-settings__top-row__quick-select__header__text" }
const _hoisted_8 = { class: "menu-calendar-settings__top-row__quick-select__buttons" }
const _hoisted_9 = { class: "calendar-footer" }
const _hoisted_10 = { class: "input-container" }
const _hoisted_11 = { class: "input-container__help-message" }
const _hoisted_12 = { class: "row" }
const _hoisted_13 = { class: "button-container" }
const _hoisted_14 = { class: "row" }

import LayoutOverlay from '@/design-system/templates/LayoutOverlay.vue'
import CalendarView from '@/design-system/molecules/CalendarView.vue'
import ButtonCalendarQuickSelect from '@/design-system/atoms/buttons/ButtonCalendarQuickSelect.vue'
import ButtonPrimary from '@/design-system/atoms/buttons/ButtonPrimary.vue'
import ButtonOutline from '@/design-system/atoms/buttons/ButtonOutline.vue'
import CheckBoxStandard from '@/design-system/atoms/CheckBoxStandard.vue'
import InputCalendarDate from '@/design-system/atoms/InputCalendarDate.vue'
import InfoLabel from '@/design-system/molecules/InfoLabel.vue'
import IconSmallArrowRight from '@/design-system/assets/icons/IconSmallArrowRight.vue'
import { computed, onMounted, ref, watchEffect } from 'vue'
import { useI18n } from 'vue-i18n'
import { format } from 'date-fns'
import { LOCALES } from '@/utils/locales'
import { useCurrentFeatureFilters } from '@/composables/useFilters'
import { QuickSelect, QUICK_SELECTS, QuickSelectType } from '../../types'
import { quickSelectToLastQuarter, quickSelectToTimeBefore } from '@/utils/calendar-utils'
import { useTimeSelection } from '@/composables/useTimeRange'

interface EMITS {
  (e: 'calendarClose'): void
  (e: 'calendarSubmit'): void
}

type QuickSelectWithIcon = {
  quickSelect: QuickSelect
  icon: string
}

type Option = {
  name: string
  title: string
  subtitle: string
  src: string
  onClick: () => void
}


export default /*@__PURE__*/_defineComponent({
  __name: 'DashboardCalendar',
  emits: ["calendarClose", "calendarSubmit"],
  setup(__props, { emit: __emit }) {

const { t, locale } = useI18n()

const filters = useCurrentFeatureFilters()
const timeSelection = useTimeSelection()

const quarterIcons: Record<number, string> = {
  1: 'Icon-Q1_x32',
  2: 'Icon-Q2_x32',
  3: 'Icon-Q3_x32',
  4: 'Icon-Q4_x32'
}

const quickSelectsWithIcon: QuickSelectWithIcon[] = [
  {
    quickSelect: QUICK_SELECTS[0],
    icon: 'Icon-Last-1-Days_x32'
  },
  {
    quickSelect: QUICK_SELECTS[1],
    icon: 'Icon-Last-7-Days_x32'
  },
  {
    quickSelect: QUICK_SELECTS[2],
    icon: 'Icon-Last-30-Days_x32'
  },
  {
    quickSelect: QUICK_SELECTS[3],
    icon: quarterIcons[quickSelectToLastQuarter(new Date(), QUICK_SELECTS[3])]
  },
  {
    quickSelect: QUICK_SELECTS[4],
    icon: 'Icon-Last-12-Months_x32'
  },
  {
    quickSelect: QUICK_SELECTS[5],
    icon: 'Icon-Current-Year_x32'
  },
  {
    quickSelect: QUICK_SELECTS[6],
    icon: 'Icon-Previous-Year_x32'
  }
]

function dateFormatter(value: string) {
  return format(new Date(value), 'yyyy-MM-dd')
}

const quickSelect = ref<QuickSelect | undefined | null>()
const onlyBusinessDays = ref<boolean>(false)
const onlyBusinessHours = ref<boolean>(false)
const startDate = ref<string>()
const endDate = ref<string | undefined>()
const selectedOption = ref<string | undefined | null>()
const snapshot = ref<any | undefined>(undefined)

const options = computed<Option[]>(() =>
  quickSelectsWithIcon.map((item) => {
    const timeEntry = quickSelectToTimeBefore(new Date(), item.quickSelect, LOCALES[locale.value])
    return {
      name: item.quickSelect.name,
      title: item.quickSelect.title[locale.value],
      subtitle: timeEntry.formatted,
      src: item.icon,
      onClick: () => {
        startDate.value = dateFormatter(timeEntry.from)
        endDate.value = dateFormatter(timeEntry.to)
        selectedOption.value = item.quickSelect.name
      }
    }
  })
)

onMounted(() => {
  if (filters.value?.current && 'calendar' in filters.value.current) {
    const currentFilter = filters.value.current
    const currentCalendar = currentFilter.calendar
    onlyBusinessDays.value = 'onlyBusinessDays' in currentFilter && currentFilter.onlyBusinessDays
    onlyBusinessHours.value =
      'onlyBusinessHours' in currentFilter && currentFilter.onlyBusinessHours

    if (currentCalendar && currentCalendar.quickSelect) {
      const found = QUICK_SELECTS.find((item) => item.name === currentCalendar.quickSelect)
      if (found) {
        quickSelect.value = found
        selectedOption.value = currentCalendar.quickSelect
        const timeEntry = quickSelectToTimeBefore(
          new Date(),
          quickSelect.value,
          LOCALES[locale.value]
        )
        startDate.value = dateFormatter(timeEntry.from)
        endDate.value = dateFormatter(timeEntry.to)
      }
    } else if (currentCalendar && currentCalendar.from && currentCalendar.to) {
      startDate.value = dateFormatter(currentCalendar.from)
      endDate.value = dateFormatter(currentCalendar.to)
    }
    snapshot.value = {
      startDate: startDate.value,
      endDate: endDate.value,
      onlyBusinessDays: onlyBusinessDays.value,
      onlyBusinessHours: onlyBusinessHours.value
    }
  }
})

const hasChanges = computed(() => {
  if (!snapshot.value) {
    return false
  }
  return (
    snapshot.value.startDate !== startDate.value ||
    snapshot.value.endDate !== endDate.value ||
    snapshot.value.onlyBusinessDays !== onlyBusinessDays.value ||
    snapshot.value.onlyBusinessHours !== onlyBusinessHours.value
  )
})

const isFormValid = computed(() => {
  if (input.value.start.value && input.value.end.value) {
    const start = input.value.start.value
    const end = input.value.end.value
    if (isValidFormat(start) && isValidFormat(end) && !isValueLaterThanToday(end)) {
      return true
    }
  }
  return false
})

const input = ref<{
  start: {
    value?: string
    error?: string
  }
  end: {
    value?: string
    error?: string
  }
}>({
  start: { value: startDate.value },
  end: { value: endDate.value }
})

watchEffect(() => {
  input.value.start.value = startDate.value ? dateFormatter(startDate.value) : undefined
  input.value.end.value = endDate.value ? dateFormatter(endDate.value) : undefined
})

const emits = __emit

function dateChanged(value: { start?: string; end?: string }) {
  input.value = { start: {}, end: {} }
  startDate.value = value.start ? dateFormatter(value.start) : undefined
  endDate.value = value.end ? dateFormatter(value.end) : undefined
}

function dragChanged() {
  quickSelect.value = null
  selectedOption.value = null
}

function inputStartValueChanged(value: string) {
  quickSelect.value = null
  selectedOption.value = null
  input.value.start.error = undefined
  if (isValidFormat(value)) {
    if (input.value.end.value && input.value.end.value < value) {
      input.value.start.value = value
      input.value.start.error = t('general__text__start_date_after_end_date')
    } else {
      input.value.start.value = value
      startDate.value = value
    }
  } else {
    input.value.start.value = value
    input.value.start.error = t('general__text__invalid_date')
  }
}

function inputEndValueChanged(value: string) {
  quickSelect.value = null
  selectedOption.value = null
  input.value.end.error = undefined
  if (isValidFormat(value)) {
    if (input.value.start.value && input.value.start.value > value) {
      input.value.end.value = value
      input.value.end.error = t('general__text__end_date_before_start_date')
    } else if (isValueLaterThanToday(value)) {
      input.value.end.value = value
      input.value.end.error = t('general__text__end_date_before_start_date')
    } else {
      input.value.end.value = value
      endDate.value = value
    }
  } else {
    input.value.end.value = value
    input.value.end.error = t('general__text__invalid_date')
  }
}

function saveDates() {
  if (startDate.value && endDate.value) {
    timeSelection.set('custom', [{ absolute: startDate.value }, { absolute: endDate.value }])
  }

  if (
    filters.value?.current &&
    'calendar' in filters.value.current &&
    filters.value.current.calendar
  ) {
    filters.value.current.calendar.quickSelect = selectedOption.value
      ? (selectedOption.value as QuickSelectType)
      : undefined
    filters.value.current.calendar.from = startDate.value
      ? dateFormatter(startDate.value)
      : undefined
    filters.value.current.calendar.to = endDate.value ? dateFormatter(endDate.value) : undefined
    filters.value.resetBucketSize()

    if ('onlyBusinessDays' in filters.value.current) {
      filters.value.$patch({
        current: {
          onlyBusinessDays: onlyBusinessDays.value
        }
      })
    }

    if ('onlyBusinessHours' in filters.value.current) {
      filters.value.$patch({
        current: {
          onlyBusinessHours: onlyBusinessHours.value
        }
      })
    }
    emits('calendarSubmit')
  }
}

function isValidFormat(value: string) {
  const pattern = '^(19|20)\\d\\d[-](0[1-9]|1[012])[-](0[1-9]|[12][0-9]|3[01])$'
  const regex: RegExp = new RegExp(pattern)
  return regex.test(value)
}

function isValueLaterThanToday(value: string) {
  return value > dateFormatter(new Date().toISOString())
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(LayoutOverlay, {
    class: "menu-calendar",
    onCloseOverlay: _cache[3] || (_cache[3] = ($event: any) => (emits('calendarClose')))
  }, {
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          _createElementVNode("div", _hoisted_3, [
            _createVNode(CalendarView, {
              startDate: startDate.value,
              endDate: endDate.value,
              onDateChanged: dateChanged,
              onDragChanged: dragChanged
            }, null, 8, ["startDate", "endDate"]),
            _createElementVNode("div", _hoisted_4, [
              (_unref(filters) && 'onlyBusinessDays' in _unref(filters).current)
                ? (_openBlock(), _createBlock(CheckBoxStandard, {
                    key: 0,
                    checked: onlyBusinessDays.value,
                    title: _unref(t)('general__text__business-days-only'),
                    description: _unref(t)('general__text__mon-fri'),
                    onValueChanged: _cache[0] || (_cache[0] = () => (onlyBusinessDays.value = !onlyBusinessDays.value))
                  }, null, 8, ["checked", "title", "description"]))
                : _createCommentVNode("", true),
              (_unref(filters) && 'onlyBusinessHours' in _unref(filters).current)
                ? (_openBlock(), _createBlock(CheckBoxStandard, {
                    key: 1,
                    checked: onlyBusinessHours.value,
                    title: _unref(t)('general__text__work-hours-only'),
                    description: _unref(t)('general__text__work-hours'),
                    onValueChanged: _cache[1] || (_cache[1] = () => (onlyBusinessHours.value = !onlyBusinessHours.value))
                  }, null, 8, ["checked", "title", "description"]))
                : _createCommentVNode("", true)
            ])
          ]),
          _createElementVNode("div", _hoisted_5, [
            _createElementVNode("div", _hoisted_6, [
              _createElementVNode("span", _hoisted_7, [
                _createVNode(InfoLabel, {
                  title: _unref(t)('general__text__quickselect'),
                  info: _unref(t)('tooltip__calendar')
                }, null, 8, ["title", "info"])
              ])
            ]),
            _createElementVNode("div", _hoisted_8, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(options.value, (option) => {
                return (_openBlock(), _createBlock(ButtonCalendarQuickSelect, _mergeProps({
                  key: option.name,
                  selected: option.name == selectedOption.value
                }, { ref_for: true }, option, {
                  onOnClicked: option.onClick
                }), null, 16, ["selected", "onOnClicked"]))
              }), 128))
            ])
          ])
        ]),
        _createElementVNode("div", _hoisted_9, [
          _createElementVNode("div", _hoisted_10, [
            _createElementVNode("span", _hoisted_11, _toDisplayString(_unref(t)('general__text__calendar_input_msg')), 1),
            _createElementVNode("div", _hoisted_12, [
              _createVNode(InputCalendarDate, {
                inputValue: input.value.start.value,
                error: input.value.start.error,
                onInputValueChanged: inputStartValueChanged
              }, null, 8, ["inputValue", "error"]),
              _createElementVNode("span", null, [
                _createVNode(IconSmallArrowRight)
              ]),
              _createVNode(InputCalendarDate, {
                inputValue: input.value.end.value,
                error: input.value.end.error,
                onInputValueChanged: inputEndValueChanged
              }, null, 8, ["inputValue", "error"])
            ])
          ]),
          _createElementVNode("div", _hoisted_13, [
            _createElementVNode("div", _hoisted_14, [
              _createVNode(ButtonOutline, {
                class: "button",
                buttonText: _unref(t)('general__text__cancel'),
                onOnClicked: _cache[2] || (_cache[2] = () => _ctx.$emit('calendarClose'))
              }, null, 8, ["buttonText"]),
              _createVNode(ButtonPrimary, {
                class: "button",
                buttonText: _unref(t)('general__text__done'),
                disabled: !hasChanges.value || !isFormValid.value,
                onOnClicked: saveDates
              }, null, 8, ["buttonText", "disabled"])
            ])
          ])
        ])
      ])
    ]),
    _: 1
  }))
}
}

})